import {
  Line2D as Line2DInterface,
  Segment2D as LineSegment2DInterface,
  WorkLine,
  WorkLineBase,
} from '@paragon/contract-library';
import { Line2D, LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';

import { clipLineAroundPoint } from '../../../three/util/infiniteLines';

export * from './insertionWorkLine';
export * from './workLineInference';

/** Builds a `WorkLine` from actualized geometry, choosing the segment or infinite-line shape as appropriate. */
export function buildWorkLineFromGeometry(
  geometry: Line2DInterface | LineSegment2DInterface,
  properties: WorkLineBase,
): WorkLine {
  return {
    ...properties,
    ...('endPoint' in geometry
      ? { geometry, geometryType: 'Segment' as const }
      : { geometry, geometryType: 'Line' as const }),
  };
}

export function isInfiniteWorkLine(line: WorkLine): line is Extract<WorkLine, { geometryType: 'Line' }> {
  return line.geometryType === 'Line';
}

export function calculateWorkLineVisualSegment(line: WorkLine, viewCenter: Point2D) {
  return isInfiniteWorkLine(line) ? clipLineAroundPoint(Line2D.fromObject(line.geometry), viewCenter) : line.geometry;
}

export function calculateWorkLineVisualHitboxPolygon(segment: LineSegment2D, antiZoomFactor: number) {
  const offset = antiZoomFactor / 2;

  // At a very high zoom level, the offset can be so small that the polygon will be considered invalid due to distance
  //   tolerance. A minimum offset would produce a uselessly big hitbox at these zoom levels, so just return null.
  if (offset <= 1 / 32) return null;

  return (
    Polygon2D.fromVerticesIfValid(
      ([] as Point2D[])
        .concat(segment.translate(segment.direction.rotateQuarterTurn().toVector(offset)).endPoints)
        .concat(segment.translate(segment.direction.rotateReverseQuarterTurn().toVector(offset)).endPoints.reverse()),
    )?.to3D(0) ?? null
  );
}
